import React from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import PageHeader from '../../components/Shared/PageHeader'

import SharedButton from "../../components/Shared/SharedButton";
import TextInput from "../../components/Shared/TextInput";
import DropdownComponent from "../../components/Shared/DropdownComponent";
import {
  CreateCustomers,
  getCustomersList,
  editCustomer,
} from "../../store/slices/CustomerSlice";
import nationalitiesData from "../../uitls/NationalitiesData.json";
import { FaCheckDouble } from "react-icons/fa6";
import CustomSearchDropdown from "../../components/Shared/CustomSearchDropdown";

export default function CreateCustomer({
  createDialogVisible,
  setCreateDialogVisible,
}) {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.customers);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location?.state?.editData || null;
  const initialValues = {
    name: editData?.name || "",
    primary_contact_number: editData?.primary_contact_number || "",
    primary_email_id: editData?.primary_email_id || "",
    status: editData?.status || "",
    address: editData?.address || "",
    document_id: editData?.document_id || "",
    nationality: editData?.nationality || "",
    branch_id: editData?.branch_id || user?.branch_id,
    business_id: editData?.business_id || user?.business_id,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t("customer_name_is_required")),
    primary_contact_number: Yup.string().required(
      t("primary_contact_number_req")
    ),
    status: Yup.string().required(t("status_is_required")),
    address: Yup.string()
      .required(t("address_is_required"))
      .matches(/^(?!\d+$).+$/, t("invalid_address_format")),
    document_id: Yup.string().required(t("document_id_is_required")),
    nationality: Yup.string().required(t("nationality_is_required")),
  });

  const status_option = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "In-Active" },
  ];
  const nationality_option = nationalitiesData?.countries?.map(country => ({
    name: country.label,
    id: country.value
  }));

  const onSubmit = async (data, { resetForm }) => {
    try {
      if (editData) {
        const resultAction = await dispatch(
          editCustomer({ data: data, id: editData?.id })
        );
        if (editCustomer.fulfilled.match(resultAction)) {
          if (setCreateDialogVisible && typeof setCreateDialogVisible === 'function') {
            setCreateDialogVisible(false);
            dispatch(getCustomersList({ page: 1, per_page: 10 }));
          } else {
            navigate('/manager/customers');
          }
          resetForm();
        }
      } else {
        const resultAction = await dispatch(CreateCustomers(data));
        if (CreateCustomers.fulfilled.match(resultAction)) {
          if (setCreateDialogVisible && typeof setCreateDialogVisible === 'function') {
            setCreateDialogVisible(false);
            dispatch(getCustomersList({ page: 1, per_page: 10 }));
          } else {
            navigate('/manager/customers');
          }
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error creating customer:", error);
    }
  };

  return (
    <div className="flex flex-col w-full gap-2 max-h-[calc(100vh-150px)]">
      <PageHeader title={t(editData ? 'Update Customer' : 'Create Customer')} backPath={'/manager/customers'} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
              <TextInput
                label={t("name")}
                name={"name"}
                placeholder={t("name_placeholder")}
                error={formik.touched.name && formik.errors.name}
              />

              <TextInput
                type={"tel"}
                label={t("primary_contact_number")}
                name={"primary_contact_number"}
                placeholder={t("primary_contact_number_placeholder")}
                error={
                  formik.touched.primary_contact_number &&
                  formik.errors.primary_contact_number
                }
              />

              <TextInput
                type={"email"}
                label={t("primary_email_id")}
                name={"primary_email_id"}
                placeholder={t("primary_email_id_placeholder")}
                error={formik.touched.primary_email_id && formik.errors.primary_email_id}
              />

              <TextInput
                label={t("address")}
                placeholder={`${t("enter")} ${t("address")}`}
                name={"address"}
                error={formik.touched.address && formik.errors.address}
              />

              <TextInput
                label={t("document_id")}
                name={"document_id"}
                placeholder={`${t("enter")} ${t("document_id")}`}
                error={formik.touched.document_id && formik.errors.document_id}
              />

              <DropdownComponent
                label={t("status")}
                name={"status"}
                placeholder={t("select_status")}
                options={status_option}
                error={formik.touched.status && formik.errors.status}
              />

              <CustomSearchDropdown
                label={t("nationality")}
                name={"nationality"}
                placeholder={t("select_nationality")}
                options={nationality_option}
                error={formik.touched.nationality && formik.errors.nationality}
              />
            </div>

            <div className="flex justify-end gap-2">
              <SharedButton
                type="button"
                icon="pi pi-times"
                label={t("Cancel")}
                buttonType="reset"
                onClick={() => navigate("/manager/customers")}
              />
              <SharedButton
                type="submit"
                icon={<FaCheckDouble size={12} className='me-2' />}
                disabled={isLoading}
                loading={isLoading}
                label={editData ? t("Update") : t("Submit")}
                className="submit-btn"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}